import React from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { geocodeAddress } from '../../../services/geographicService';
import type { SelfServiceFormData } from '../../../types/selfService';
import { findStateForPoint } from './findStateForPoint';
import { useUSStatesGeo } from './useUSStatesGeo';

interface AddressLeadInProps {
  accentColor: string;
  // Called with the 2-letter state code derived from the geocoded address
  // (point-in-polygon against the US states GeoJSON). Null when the point
  // doesn't fall inside any US state polygon, or when the GeoJSON hasn't
  // loaded yet.
  onDerivedStateChange?: (stateCode: string | null) => void;
  // CTV-489: optional one-click suggestion offered while the input is
  // empty — typically the station's home-market center, so a buyer who
  // doesn't have a specific business address can still center the radius
  // tool on the publisher's signal without typing.
  suggestedCenter?: { lat: number; lng: number; label: string } | null;
}

// Shared "Business address" lead-in for the geo-targeting tabs. Geocoding
// here writes geoTargetCenter into form state, which Radius reads. The
// derived state (the US state containing the geocoded lat/lng) is surfaced
// so the State tab can offer it as a one-click suggestion.
const AddressLeadIn: React.FC<AddressLeadInProps> = ({ accentColor, onDerivedStateChange, suggestedCenter }) => {
  const { watch, setValue } = useFormContext<SelfServiceFormData>();
  const center = watch('geoTargetCenter');
  const [input, setInput] = React.useState('');
  const [geocoding, setGeocoding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { data: statesGeo } = useUSStatesGeo();

  // Derive the containing state whenever center or the states GeoJSON
  // changes. Cheap (point-in-polygon over ~50 features); fine to recompute.
  const derivedState = React.useMemo(() => {
    if (!center || !statesGeo) return null;
    return findStateForPoint(center.lng, center.lat, statesGeo);
  }, [center, statesGeo]);

  React.useEffect(() => {
    onDerivedStateChange?.(derivedState);
  }, [derivedState, onDerivedStateChange]);

  const handleGeocode = async () => {
    const text = input.trim();
    if (!text) return;
    setGeocoding(true);
    setError(null);
    try {
      const result = await geocodeAddress(text);
      if (!result) {
        setError('Could not find that address. Try adding a city or ZIP.');
        return;
      }
      setValue('geoTargetCenter', result, { shouldDirty: true });
    } catch (err) {
      console.error('[AddressLeadIn] geocode error', err);
      const detail = err instanceof Error ? err.message : 'Try again.';
      setError(`Address lookup failed: ${detail}`);
    } finally {
      setGeocoding(false);
    }
  };

  const handleClear = () => {
    setValue('geoTargetCenter', null, { shouldDirty: true });
    setValue('geoTargetZips', [], { shouldDirty: true });
    // Drop the cached fake-calc minimum so the next geocode replays the
    // spinner + recomputes (budget/days may have changed in the interim).
    setValue('geoTargetComputedMinRadius', undefined, { shouldDirty: true });
    setInput('');
    setError(null);
  };

  if (center) {
    return (
      <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
        <div className="flex items-start gap-2 min-w-0">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-slate-900 truncate">{center.label}</div>
            {derivedState && (
              <div className="text-[11px] text-slate-500">
                <span className="text-slate-700">{derivedState}</span>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="text-[12px] text-slate-500 hover:text-red-600 underline shrink-0"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-slate-700">Business location</label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleGeocode();
              }
            }}
            placeholder="City, ZIP, or full address"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{ ['--tw-ring-color' as string]: accentColor }}
          />
        </div>
        <button
          type="button"
          onClick={handleGeocode}
          disabled={!input.trim() || geocoding}
          className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          style={{ backgroundColor: accentColor }}
        >
          {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Find
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {suggestedCenter && (
        <button
          type="button"
          onClick={() =>
            setValue('geoTargetCenter', suggestedCenter, { shouldDirty: true })
          }
          className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          <MapPin className="w-3 h-3" style={{ color: accentColor }} />
          Or use {suggestedCenter.label}
        </button>
      )}
    </div>
  );
};

export default AddressLeadIn;
